/**
 * QuotesPanel — track supplier quotes and award per line item.
 *
 * Quotes are captured per supplier (scoped to the line items that supplier
 * covers) via the "Record quote" form, then compared and awarded per line
 * item, so the assembled bid can combine multiple manufacturers.
 */
import React, { useState } from 'react';
import { CheckCircle2, Circle, Award, Layers, Plus, X } from 'lucide-react';
import { forgeAPI } from '../../../services/api';
import { useForge } from '../ForgeContext';

const money = (n) => (n == null ? '—' : `CAD ${Number(n).toLocaleString()}`);

const RecordQuoteForm = ({ tenderId, accepted, specLines, onDone }) => {
  const [supplierId, setSupplierId] = useState('');
  const [freight, setFreight] = useState('');
  const [lines, setLines] = useState({});
  const [busy, setBusy] = useState(false);

  const supplier = accepted.find((c) => c.candidate_id === supplierId);
  const coveredLines = supplier
    ? specLines.filter((l) => (supplier.line_items || []).includes(l.line_item))
    : [];

  const setLine = (n, field, val) =>
    setLines((p) => ({ ...p, [n]: { ...(p[n] || {}), [field]: val } }));

  const submit = async () => {
    if (!supplier || !coveredLines.length) return;
    setBusy(true);
    try {
      await forgeAPI.intakeQuote(tenderId, {
        supplier_id: supplier.candidate_id,
        supplier_name: supplier.company,
        currency: 'USD',
        freight_cad: parseFloat(freight) || null,
        source_doc: 'manual entry',
        line_items: coveredLines.map((l) => {
          const v = lines[l.line_item] || {};
          const qty = (l.quote_quantities || []).slice(-1)[0] || null;
          return {
            line_item: l.line_item,
            sku: l.sku,
            product_name: l.product_name,
            quantity: qty,
            unit_price: v.unit_price ? parseFloat(v.unit_price) : null,
            currency: v.currency || 'USD',
            moq: v.moq ? parseFloat(v.moq) : null,
            lead_time_weeks: v.lead_time_weeks ? parseFloat(v.lead_time_weeks) : null,
            terms: v.terms || 'DDP',
          };
        }),
      });
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="forge-quote-form">
      <div className="forge-add-row">
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
          <option value="">Select supplier…</option>
          {accepted.map((c) => (
            <option key={c.candidate_id} value={c.candidate_id}>{c.company}</option>
          ))}
        </select>
        <input placeholder="Freight (CAD)" value={freight} onChange={(e) => setFreight(e.target.value)} style={{ width: 130 }} />
      </div>
      {coveredLines.length > 0 && (
        <table className="products-table forge-quote-form-table">
          <thead>
            <tr><th>Line item</th><th>Unit price</th><th>Currency</th><th>MOQ</th><th>Lead (wk)</th><th>Terms</th></tr>
          </thead>
          <tbody>
            {coveredLines.map((l) => {
              const v = lines[l.line_item] || {};
              return (
                <tr key={l.line_item}>
                  <td>#{l.line_item} {l.product_name}</td>
                  <td><input value={v.unit_price || ''} onChange={(e) => setLine(l.line_item, 'unit_price', e.target.value)} style={{ width: 80 }} /></td>
                  <td>
                    <select value={v.currency || 'USD'} onChange={(e) => setLine(l.line_item, 'currency', e.target.value)}>
                      <option>USD</option><option>CAD</option>
                    </select>
                  </td>
                  <td><input value={v.moq || ''} onChange={(e) => setLine(l.line_item, 'moq', e.target.value)} style={{ width: 60 }} /></td>
                  <td><input value={v.lead_time_weeks || ''} onChange={(e) => setLine(l.line_item, 'lead_time_weeks', e.target.value)} style={{ width: 50 }} /></td>
                  <td><input value={v.terms || ''} placeholder="DDP" onChange={(e) => setLine(l.line_item, 'terms', e.target.value)} style={{ width: 70 }} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {supplier && !coveredLines.length && (
        <p className="forge-muted">This supplier has no line items assigned — set its coverage in the Shortlist tab.</p>
      )}
      <div className="forge-quote-form-actions">
        <button className="forge-btn-primary" onClick={submit} disabled={busy || !coveredLines.length}>
          {busy ? 'Saving…' : 'Save quote'}
        </button>
      </div>
    </div>
  );
};

const QuotesPanel = ({ onGoToTab }) => {
  const { tenderId, summary, refresh } = useForge();
  const lineItems = summary?.quotes?.line_items || [];
  const bid = summary?.bid_summary;
  const accepted = (summary?.shortlist || []).filter((c) => c.status === 'accepted');
  const specLines = summary?.spec_sheet?.line_items || [];
  const [busy, setBusy] = useState(null);
  const [recording, setRecording] = useState(false);

  const award = async (lineItem, quoteId) => {
    setBusy(`${lineItem}:${quoteId}`);
    await forgeAPI.awardLineItem(tenderId, lineItem, quoteId);
    await refresh();
    setBusy(null);
  };
  const clear = async (lineItem) => {
    setBusy(`clear:${lineItem}`);
    await forgeAPI.clearAward(tenderId, lineItem);
    await refresh();
    setBusy(null);
  };

  const totalQuotes = lineItems.reduce((n, li) => n + (li.quotes?.length || 0), 0);

  return (
    <div className="forge-quotes">
      <div className="forge-spec-toolbar">
        <div className="forge-muted">{totalQuotes} quote line{totalQuotes === 1 ? '' : 's'} across {accepted.length} accepted supplier{accepted.length === 1 ? '' : 's'}</div>
        <button className="forge-btn-ghost" onClick={() => setRecording((r) => !r)}>
          {recording ? <X size={14} /> : <Plus size={14} />} {recording ? 'Close' : 'Record quote'}
        </button>
      </div>

      {recording && (
        <RecordQuoteForm
          tenderId={tenderId} accepted={accepted} specLines={specLines}
          onDone={async () => { setRecording(false); await refresh(); }}
        />
      )}

      {/* Mixed-supplier bid summary */}
      {bid && totalQuotes > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title"><Layers size={15} /> Assembled bid</h4>
          <div className="decision-grid">
            <div className={`decision-card ${bid.complete ? 'green' : 'amber'}`}>
              <div className="decision-header"><span className="decision-type">Line items awarded</span></div>
              <p className="decision-reason" style={{ fontWeight: 700 }}>{bid.awarded_line_items}/{bid.total_line_items}</p>
            </div>
            <div className="decision-card green">
              <div className="decision-header"><span className="decision-type">Suppliers used</span></div>
              <p className="decision-reason" style={{ fontWeight: 600 }}>
                {bid.supplier_count || 0}{bid.suppliers?.length ? ` — ${bid.suppliers.join(', ')}` : ''}
              </p>
            </div>
            <div className="decision-card green">
              <div className="decision-header"><span className="decision-type">Total landed (CAD)</span></div>
              <p className="decision-reason" style={{ fontWeight: 700 }}>{money(bid.total_landed_cad)}</p>
              <span className="forge-muted">goods {money(bid.goods_total_cad)} + freight {money(bid.freight_total_cad)}</span>
            </div>
          </div>
        </div>
      )}

      {totalQuotes === 0 && !recording && (
        <div className="forge-empty">
          No quotes yet. Click <b>Record quote</b> to log a supplier's pricing (per line item),
          or capture it from a conversation in the Inbox.
        </div>
      )}

      {/* Per-line-item comparison + award */}
      {totalQuotes > 0 && lineItems.map((li) => (
        <div key={li.line_item} className="summary-section">
          <h4 className="summary-section-title">
            <span className="forge-ds-num">#{li.line_item}</span> {li.product_name}
            {li.sku && <span className="heuristic-chip">{li.sku}</span>}
            {li.awarded_quote_id
              ? <span className="forge-status forge-status-validated">awarded</span>
              : <span className="forge-status forge-status-needs_attention">not awarded</span>}
          </h4>
          {li.quotes.length === 0 ? (
            <p className="forge-muted">No supplier has quoted this item yet.</p>
          ) : (
            <table className="products-table">
              <thead>
                <tr><th></th><th>Factory</th><th>Unit price</th><th>Unit (CAD)</th><th>MOQ</th>
                  <th>Lead time</th><th>Terms</th><th>Reliability</th><th></th></tr>
              </thead>
              <tbody>
                {li.quotes.map((q) => (
                  <tr key={q.quote_id} className={q.awarded ? 'forge-row-selected' : ''}>
                    <td>{q.awarded ? <CheckCircle2 size={16} className="forge-ok-ic" /> : <Circle size={15} className="forge-muted" />}</td>
                    <td style={{ fontWeight: 600 }}>{q.factory}</td>
                    <td>{q.unit_price == null ? '—' : `${q.currency} ${q.unit_price}`}</td>
                    <td><b>{money(q.unit_price_cad)}</b></td>
                    <td>{q.moq ?? '—'}</td>
                    <td>{q.lead_time_weeks ? `${q.lead_time_weeks} wk` : '—'}</td>
                    <td>{q.terms || '—'}</td>
                    <td><span className={`forge-rel forge-rel-${q.reliability_band || 'unknown'}`}>
                      {q.reliability == null ? '—' : Math.round(q.reliability)}</span></td>
                    <td>
                      {q.awarded ? (
                        <button className="forge-btn-sm forge-btn-no" disabled={busy}
                          onClick={() => clear(li.line_item)}>Unaward</button>
                      ) : (
                        <button className="forge-btn-sm forge-btn-primary"
                          disabled={busy === `${li.line_item}:${q.quote_id}`}
                          onClick={() => award(li.line_item, q.quote_id)}>
                          <Award size={13} /> Award
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}

      {totalQuotes > 0 && (
        <button className="forge-btn-ghost" onClick={() => onGoToTab && onGoToTab('validate')}>
          Go to validation →
        </button>
      )}
    </div>
  );
};

export default QuotesPanel;
